import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Send, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export const ReviewPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/bookings/${bookingId}/review`);
      setBooking(response.data.booking);

      // Pre-fill name from booking
      if (response.data.booking) {
        setName(`${response.data.booking.firstName} ${response.data.booking.lastName}`);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError(
        err.response?.data?.detail ||
        "Cette réservation n'existe pas ou n'est pas encore terminée."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (rating === 0) {
      toast.error('Veuillez donner une note');
      return;
    }

    if (!comment.trim()) {
      toast.error('Veuillez laisser un commentaire');
      return;
    }

    try {
      setSubmitting(true);

      await api.post('/api/testimonials', {
        name: name.trim(),
        rating,
        comment: comment.trim(),
        bookingId: bookingId,
      });

      setSubmitted(true);
      toast.success('Merci pour votre avis ! 💖');

      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error submitting review:', err);
      toast.error(
        err.response?.data?.detail ||
        "Une erreur s'est produite lors de l'envoi de votre avis"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0EB]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#A67B5B]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0EB] px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h1 className="text-2xl font-serif font-bold text-[#2C1810] mb-4">
            Oups !
          </h1>
          <p className="text-[#5D4037] mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary px-8 py-3 rounded-full font-semibold"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0EB] px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h1 className="text-2xl font-serif font-bold text-[#2C1810] mb-4">
            Merci pour votre avis ! 💖
          </h1>
          <p className="text-[#5D4037] mb-2">
            Votre retour d'expérience est très précieux pour nous et pour nos futures clientes.
          </p>
          <p className="text-sm text-[#A67B5B]">
            Redirection vers l'accueil...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0EB] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C9A96E] to-[#A67B5B] flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl font-serif">MB</span>
          </div>
          <h1 className="text-4xl font-serif font-bold text-[#2C1810] mb-2">
            Partagez votre expérience
          </h1>
          <p className="text-[#A67B5B]">
            Votre avis nous aide à nous améliorer continuellement
          </p>
        </div>

        {/* Booking Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="font-semibold text-[#C9A96E] mb-3 font-serif text-lg">
            Votre prestation
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#A67B5B]">Service :</span>
              <span className="text-[#2C1810] font-medium">{booking.serviceName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#A67B5B]">Date :</span>
              <span className="text-[#2C1810] font-medium">
                {new Date(booking.date).toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-[#2C1810] mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E]"
                placeholder="Votre nom"
                required
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-[#2C1810] mb-3">
                Note <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      size={40}
                      className={
                        star <= (hoverRating || rating)
                          ? 'fill-[#FFB300] text-[#FFB300]'
                          : 'text-[#D4C4B5]'
                      }
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-[#A67B5B] mt-2">
                  {rating === 5 && '⭐ Excellent !'}
                  {rating === 4 && '😊 Très bien !'}
                  {rating === 3 && '🙂 Bien'}
                  {rating === 2 && '😕 Moyen'}
                  {rating === 1 && '😞 Décevant'}
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-semibold text-[#2C1810] mb-2">
                Votre commentaire <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 border border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E] resize-none"
                placeholder="Parlez-nous de votre expérience chez Miss Baleng..."
                required
              />
              <p className="text-xs text-[#A67B5B] mt-1">
                Minimum 10 caractères
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-[#FFF4E6] border border-[#FFE0B2] rounded-lg p-4">
              <p className="text-sm text-[#5D4037]">
                💡 <strong>Votre avis sera publié sur notre site</strong> après validation.
                Il aidera d'autres personnes à découvrir nos services.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || rating === 0 || !comment.trim()}
              className="w-full btn-primary py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Envoyer mon avis
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-[#A67B5B] mt-6">
          Merci de prendre le temps de partager votre expérience ! 💖
        </p>
      </div>
    </div>
  );
};
